import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Breadcrumbs, Button, CircularProgress, Link, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AuditStatusChip } from "../AuditHub/components/AuditCells";
import { daysRemainingTone, formatDaysRemaining, formatShortDate } from "../AuditHub/utils";
import { AH_ACCENT, AH_MUTED, AH_TEXT, MetaSeparator, backButtonSx } from "../AuditHub/theme";
import { frameworkMatches } from "./ControlAuditPanel";
import { EMPTY_COMPONENTS, addComponents, complianceScoreParts, readComponents } from "./auditMetrics";

const DUE_COLOR = { overdue: "#dc2626", warn: "#d97706" };

const factSx = { fontSize: 13, color: AH_MUTED, flexShrink: 0, whiteSpace: "nowrap" };

/** Visible assessment name and owner stay within this many characters. */
const VALUE_LIMIT = 10;

const valueSx = {
  fontSize: 13,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: "22px",
  flexShrink: 0,
  whiteSpace: "nowrap",
  cursor: "default",
  borderRadius: "4px",
  "&:focus-visible": { outline: `2px solid ${AH_ACCENT}`, outlineOffset: 2 },
};

const clipToLimit = (value) => {
  const chars = Array.from(value);
  if (chars.length <= VALUE_LIMIT) return value;
  return `${chars.slice(0, VALUE_LIMIT).join("")}...`;
};

/** Plain text for a context value: 10 characters on screen, full value in the tooltip. */
const TruncatedFact = ({ label, value, emptyTip, prefix, plain = false, tip = null, color }) => {
  const full = String(value ?? "").trim();
  const title = tip || full || emptyTip;
  const shown = full ? clipToLimit(full) : "—";
  return (
    <Tooltip title={title} arrow placement="bottom">
      <Typography
        component="span"
        tabIndex={0}
        aria-label={`${label}: ${full || emptyTip}`}
        sx={plain ? { ...valueSx, fontWeight: 400, color: color || AH_MUTED } : { ...valueSx, ...(color ? { color } : null) }}
      >
        {prefix}
        {shown}
      </Typography>
    </Tooltip>
  );
};

/** Same figure as the assessment Compliance Score ring: passed / total for this framework, N/A excluded. */
const frameworkComplianceScore = (accounts, compliance) => {
  const components = (Array.isArray(accounts) ? accounts : []).reduce((total, account) => {
    const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
    return scope ? addComponents(total, readComponents(scope.components)) : total;
  }, EMPTY_COMPONENTS);
  return complianceScoreParts(components).score;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Account option state for the current framework: the view needs that account's scope and a finished run. */
const accountAvailability = (account, compliance) => {
  const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
  if (!scope) return { scope: null, reason: "Framework not assessed on this account", pendingScan: false };
  if (!scope.latestRunId) return { scope, reason: "Pending Automated Scan Completion", pendingScan: true };
  return { scope, reason: "", pendingScan: false };
};

/** One row: breadcrumb, audit name and summary, and Back - in the Audit Hub style. */
const AuditContextBar = ({ auditId, state, compliance }) => {
  const history = useHistory();

  if (!auditId) return null;

  const audit = state?.audit;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const accountCount = audit?.accountCount ?? accounts.length;
  const loading = state?.status === "loading" || state?.status === "idle";
  const goToAuditHub = () => history.push("/audit-hub");

  const auditorEmails = (Array.isArray(audit?.auditorEmails) ? audit.auditorEmails : [])
    .map((email) => String(email ?? "").trim())
    .filter(Boolean);
  const auditorTip = auditorEmails.length ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {auditorEmails.map((email, index) => (
        <span key={`${email}-${index}`}>{email}</span>
      ))}
    </Box>
  ) : null;
  const remaining = audit?.dueDate ? formatDaysRemaining(audit) : "—";
  const scanPending = audit?.auditScore == null;
  const frameworkScore = frameworkComplianceScore(accounts, compliance);
  const scoreValue = Number.isFinite(frameworkScore) ? `${frameworkScore.toFixed(2)}%` : "—";
  const facts = [
    { key: "accounts", text: plural(accountCount, "account") },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: 1.5,
        flexWrap: "nowrap",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: 1.25,
          flexWrap: "nowrap",
          minWidth: 0,
          flex: "1 1 auto",
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        <Breadcrumbs
          sx={{
            fontSize: 13,
            color: AH_MUTED,
            flexShrink: 0,
            "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", alignItems: "center" },
            "& .MuiBreadcrumbs-separator": { mx: 0.5 },
          }}
        >
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={goToAuditHub}
            sx={{ fontSize: 13, p: 0, lineHeight: "22px", verticalAlign: "baseline" }}
          >
            Assessments
          </Link>
          <Typography sx={{ fontSize: 13, color: AH_TEXT, lineHeight: "22px" }}>{audit?.auditCode || auditId}</Typography>
        </Breadcrumbs>
        <MetaSeparator hideOnMobile />
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <CircularProgress size={14} />
            <Typography noWrap sx={{ fontSize: 13, color: AH_MUTED }}>Loading assessment…</Typography>
          </Box>
        ) : state?.status === "error" ? (
          <Typography sx={{ fontSize: 13, color: "#b91c1c", minWidth: 0, overflowWrap: "anywhere" }}>{state.error}</Typography>
        ) : (
          <>
            <TruncatedFact label="Assessment Name" value={audit?.name} emptyTip="No assessment name" />
            {audit?.status ? (
              <>
                <MetaSeparator />
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                  <AuditStatusChip status={audit.status} />
                </Box>
              </>
            ) : null}
            <MetaSeparator />
            <TruncatedFact label="Owner" value={audit?.ownerEmail} emptyTip="No owner assigned" prefix="Owner: " plain />
            <MetaSeparator />
            <TruncatedFact
              label="Auditor"
              value={auditorEmails.join(", ")}
              emptyTip="No auditors assigned"
              prefix="Auditor: "
              plain
              tip={auditorTip}
            />
            <MetaSeparator />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1.25,
                flexWrap: "nowrap",
                flexShrink: 0,
              }}
            >
              <TruncatedFact
                label="Score"
                value={scanPending ? "Scan in progress" : scoreValue}
                prefix="Score: "
                plain
                color={scanPending ? AH_ACCENT : undefined}
              />
              {facts.map((item) => (
                <React.Fragment key={item.key}>
                  <MetaSeparator />
                  <Typography sx={factSx}>{item.text}</Typography>
                </React.Fragment>
              ))}
              {audit?.dueDate ? (
                <>
                  <MetaSeparator />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <TruncatedFact
                      label="Due Date"
                      value={formatShortDate(audit.dueDate)}
                      emptyTip="No due date"
                      prefix="Due Date: "
                      plain
                    />
                    {remaining !== "—" ? (
                      <Typography sx={{ ...factSx, fontWeight: 600, color: DUE_COLOR[daysRemainingTone(audit)] || "inherit" }}>
                        ({remaining})
                      </Typography>
                    ) : null}
                  </Box>
                </>
              ) : null}
            </Box>
          </>
        )}
      </Box>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={goToAuditHub} sx={backButtonSx}>
        Back
      </Button>
    </Box>
  );
};

export { accountAvailability };
export default AuditContextBar;
